import {
  API_ROUTES,
  financialPeriodSchema,
  hstReturnSchema,
  readinessItemSchema,
  returnDetailRowSchema,
  signOffCandidateSchema,
  signOffResultSchema,
  type ClosePeriodRequest,
  type FileReturnRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** The close and the return. */

export const closeKeys = {
  all: (organizationId: string) => ['close', organizationId] as const,
  periods: (organizationId: string) => ['close', organizationId, 'periods'] as const,
  readiness: (organizationId: string, periodId: string) =>
    ['close', organizationId, 'readiness', periodId] as const,
  signOff: (organizationId: string, periodId: string) =>
    ['close', organizationId, 'sign-off', periodId] as const,
  returns: (organizationId: string) => ['close', organizationId, 'returns'] as const,
  returnDetail: (organizationId: string, returnId: string, line: string) =>
    ['close', organizationId, 'return-detail', returnId, line] as const,
} as const;

export const periodsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: closeKeys.periods(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.close.periods(organizationId),
        schema: z.array(financialPeriodSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export const readinessQuery = (organizationId: string, periodId: string) =>
  queryOptions({
    queryKey: closeKeys.readiness(organizationId, periodId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.close.readiness(organizationId, periodId),
        schema: z.array(readinessItemSchema),
        signal,
      }),
    // The checklist is the live state of the books, so it goes stale the
    // moment somebody approves a document. Short on purpose.
    staleTime: 5_000,
  });

export const signOffQuery = (organizationId: string, periodId: string) =>
  queryOptions({
    queryKey: closeKeys.signOff(organizationId, periodId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.close.signOff(organizationId, periodId),
        schema: z.array(signOffCandidateSchema),
        signal,
      }),
    staleTime: 5_000,
  });

/** Signs exactly the records given, each re-checked by the database first. */
export function signOffRecords(organizationId: string, periodId: string, transactionIds: string[]) {
  return apiClient.request({
    path: API_ROUTES.close.signOff(organizationId, periodId),
    method: 'POST',
    body: { transactionIds },
    schema: signOffResultSchema,
  });
}

export const returnsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: closeKeys.returns(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.close.returns(organizationId),
        schema: z.array(hstReturnSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export const returnDetailQuery = (organizationId: string, returnId: string, line: string) =>
  queryOptions({
    queryKey: closeKeys.returnDetail(organizationId, returnId, line),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.close.returnDetail(organizationId, returnId, line),
        schema: z.array(returnDetailRowSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export function closePeriod(organizationId: string, periodId: string, body: ClosePeriodRequest) {
  return apiClient.request({
    path: API_ROUTES.close.period(organizationId, periodId),
    method: 'POST',
    body,
    schema: financialPeriodSchema,
  });
}

export function ensurePeriods(organizationId: string, fiscalYear: number) {
  return apiClient.request({
    path: API_ROUTES.close.periods(organizationId),
    method: 'POST',
    body: { fiscalYear },
    schema: z.object({ created: z.number() }),
  });
}

export function buildReturn(organizationId: string, returnId: string) {
  return apiClient.request({
    path: API_ROUTES.close.returnBuild(organizationId, returnId),
    method: 'POST',
    body: {},
    schema: hstReturnSchema,
  });
}

export function fileReturn(organizationId: string, returnId: string, body: FileReturnRequest) {
  return apiClient.request({
    path: API_ROUTES.close.returnFile(organizationId, returnId),
    method: 'POST',
    body,
    schema: hstReturnSchema,
  });
}
